import {
  AccountBalance,
  ArrowBack,
  ChevronRight,
  DeleteOutline,
  DescriptionOutlined,
  Email as EmailIcon,
  EditOutlined,
  MoreVert,
  OpenInNew,
  Person,
  Phone,
} from "@mui/icons-material";
import {
  Avatar,
  Chip,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  Tab,
  Tabs,
  Tooltip,
} from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import axios, { deleteData } from "const/axios";
import { API_URL } from "const/constants";
import DeleteModal from "components/Modals/DeleteModal";
import TaskModal from "views/admin/Tasks/TaskModal";
import { P } from "const/permissions";
import { formatDisplayDate } from "utils/dateFormat";
import { useMemo, useState } from "react";
import { FaPlusCircle } from "react-icons/fa";
import { toast } from "react-toastify";
import { Link, useNavigate, useParams } from "react-router-dom";
import { getThemedTabsSx } from "theme/tabsTheme";
import { hasPermission } from "utils/permissionUtils";
import { getModuleClientConfig } from "./moduleClientConfigs";

const getInitials = (name) =>
  (name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "?";

const MetaItem = ({ icon: Icon, children, href }) => {
  const content = (
    <>
      <Icon className="!text-[13px] shrink-0 opacity-70" />
      <span className="truncate">{children}</span>
    </>
  );
  const base = "inline-flex items-center gap-1.5 text-xs text-app-text-secondary min-w-0";
  return href ? (
    <a href={href} className={`${base} hover:text-primary transition-colors`}>
      {content}
    </a>
  ) : (
    <span className={base}>{content}</span>
  );
};

const EntryRowSkeleton = () => (
  <li className="flex items-center gap-3 px-4 py-3.5">
    <div className="min-w-0 flex-1 space-y-2">
      <Skeleton variant="rounded" width="42%" height={16} />
      <Skeleton variant="rounded" width="28%" height={12} />
    </div>
    <Skeleton variant="rounded" width={72} height={12} />
  </li>
);

/** Drill-down: one client's module entries (mirrors ClientAdmissionsPage). */
export default function ClientModuleEntriesPage({ moduleKey }) {
  const config = getModuleClientConfig(moduleKey);
  const { clientId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tabIndex, setTabIndex] = useState(0);
  const entryTabs = config?.entryTabs;

  const [anchorEl, setAnchorEl] = useState(null);
  const [menuItem, setMenuItem] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState({ state: false, id: null });
  const [assignTarget, setAssignTarget] = useState({ state: false, data: null });
  const canDelete = hasPermission(config?.deletePermission);
  const canAssignTask = hasPermission(P.TASKS.CREATE);

  // Snapshot the row into the modal payload at click time (stable reference, contextual
  // prefill): the entry id for the task link, the page client for the locked client picker,
  // and a one-line title the assigner can still edit.
  const openAssignTask = (item) => {
    const name = item?.name || clientName;
    const title = config.getEntryTitle(item);
    setAssignTarget({
      state: true,
      data: {
        ...item,
        clientId: Number(clientId),
        name,
        specificTask: `${config.taskModule}: ${title} (${name})`,
      },
    });
    closeRowMenu();
  };

  const openRowMenu = (event, item) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setMenuItem(item);
  };
  const closeRowMenu = () => {
    setAnchorEl(null);
    setMenuItem(null);
  };

  const clientAccountQuery = useQuery(
    ["module-client-profile", moduleKey, clientId],
    async () => {
      const res = await axios.get(`${API_URL}/accounts/client/${clientId}`);
      return res?.data?.data ?? null;
    },
    { enabled: !!clientId, refetchOnWindowFocus: false }
  );

  const recordsQuery = useQuery(
    [config?.recordsQueryKey, clientId],
    () => config.fetchRecordsByClientId(clientId),
    {
      enabled: !!clientId && !!config,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    }
  );

  const deletePreviewQuery = useQuery(
    ["module-delete-preview", moduleKey, deleteTarget.id],
    async () => {
      const res = await axios.get(
        `${API_URL}/${config.apiBasePath}/${deleteTarget.id}/delete-preview`
      );
      return res?.data?.data ?? null;
    },
    { enabled: Boolean(deleteTarget.state && deleteTarget.id), refetchOnWindowFocus: false }
  );

  const handleDeleteEntry = () => {
    deleteData(
      `${API_URL}/${config.apiBasePath}/${deleteTarget.id}`,
      () => {
        setDeleteTarget({ state: false, id: null });
        recordsQuery.refetch();
        queryClient.invalidateQueries([config.clientsQueryKey]);
      },
      localStorage.getItem("token")
    );
  };

  const records = recordsQuery.data ?? [];
  const activeTab = entryTabs?.[tabIndex] ?? "All";
  const filteredRecords = useMemo(
    () => records.filter((item) => config.matchesEntryTab(item, activeTab)),
    [records, activeTab, config]
  );

  const tabCounts = useMemo(() => {
    if (!entryTabs?.length) return {};
    return entryTabs.reduce((acc, tab) => {
      acc[tab] = records.filter((item) => config.matchesEntryTab(item, tab)).length;
      return acc;
    }, {});
  }, [entryTabs, records, config]);

  const clientAccount = clientAccountQuery.data;
  const clientProfile = clientAccount?.clientData ?? null;
  const clientAccountId = clientAccount?.id ?? null;
  const client = clientProfile || records[0] || {};

  const clientName = client?.name || "Client";
  const passportNumber = client?.passportNumber;
  const addState = {
    preselectClientId: Number(clientId),
    preselectClient: client,
  };

  const openEntry = (item) => {
    navigate(`${config.basePath}/edit/${item.id}`, { state: { item } });
  };

  const handleViewAccount = async () => {
    const fallbackClientData = clientProfile || {
      id: Number(clientId),
      name: clientName,
      email: client?.email,
      number: client?.number,
      caseOfficer: client?.caseOfficer,
    };
    try {
      if (clientAccountId) {
        const res = await axios.get(`${API_URL}/accounts/${clientAccountId}`);
        const account = res?.data?.data;
        navigate(`/admin/account/add?clientId=${clientId}`, {
          state: {
            item: {
              ...(account || {}),
              clientAccountId,
              hasAccountDetails: true,
              clientData: account?.clientData || fallbackClientData,
            },
          },
        });
        return;
      }
      navigate(`/admin/account/add?clientId=${clientId}`, {
        state: { item: { clientData: fallbackClientData } },
      });
    } catch (err) {
      toast.error(err?.response?.data?.errorMessage || "Unable to open linked account");
    }
  };

  const initialLoading =
    (recordsQuery.isLoading || clientAccountQuery.isLoading) && !recordsQuery.data;
  const refreshing = recordsQuery.isFetching && !!recordsQuery.data;

  if (!config) {
    return null;
  }

  return (
    <div className="mt-4 dashBody px-4 pb-12 max-w-5xl mx-auto w-full">
      <button
        type="button"
        onClick={() => navigate(config.basePath)}
        className="mb-4 inline-flex items-center gap-1 text-xs font-medium text-app-text-secondary hover:text-primary transition-colors"
      >
        <ArrowBack className="!text-sm" />
        Back to {config.title?.split("·")[0]?.trim() || "clients"}
      </button>

      <div className="rounded-xl border border-app-border bg-app-surface p-4 mb-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0">
            <Avatar
              sx={{
                bgcolor: "#ea580c",
                width: 44,
                height: 44,
                fontSize: 15,
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              {getInitials(clientName)}
            </Avatar>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-lg font-bold text-app-text truncate">{clientName}</h1>
                {passportNumber && (
                  <Chip
                    size="small"
                    label={passportNumber}
                    variant="outlined"
                    sx={{ height: 22, fontSize: 11 }}
                  />
                )}
              </div>
              <p className="text-xs text-app-text-muted mt-0.5">
                {config.entriesSectionLabel}
                {records.length > 0 ? ` · ${records.length} total` : ""}
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
                {client?.email && (
                  <MetaItem icon={EmailIcon} href={`mailto:${client.email}`}>
                    {client.email}
                  </MetaItem>
                )}
                {client?.number && <MetaItem icon={Phone}>{client.number}</MetaItem>}
                {client?.caseOfficer && <MetaItem icon={Person}>{client.caseOfficer}</MetaItem>}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => navigate(`/admin/client/view/${clientId}`)}
              className="inline-flex items-center gap-1 rounded-lg border border-app-border bg-app-surface px-3 py-2 text-xs font-medium text-app-text hover:bg-app-surface-muted transition-colors"
            >
              Client profile
              <OpenInNew className="!text-[13px]" />
            </button>
            <Link
              to={`${config.basePath}/add`}
              state={addState}
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-xs font-semibold text-white hover:opacity-90 transition-opacity shadow-sm"
            >
              <FaPlusCircle />
              {config.newEntryLabel}
            </Link>
          </div>
        </div>
      </div>

      <section className="rounded-xl border border-app-border bg-app-surface overflow-hidden">
        <div className="px-4 py-3.5 border-b border-app-border bg-gradient-to-r from-primary/[0.06] via-app-surface-muted/50 to-app-surface flex items-center justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary ring-1 ring-primary/20">
              <DescriptionOutlined sx={{ fontSize: 18 }} />
            </span>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-base font-bold text-app-text tracking-tight m-0">
                  {config.entriesSectionLabel}
                </h2>
                <span className="inline-flex items-center rounded-full border border-primary/25 bg-primary/10 px-2 py-0.5 text-[11px] font-bold text-primary tabular-nums">
                  {filteredRecords.length !== records.length
                    ? `${filteredRecords.length} / ${records.length}`
                    : records.length}
                </span>
                {refreshing && <CircularProgress size={14} className="opacity-60" />}
              </div>
              <p className="text-xs text-app-text-muted mt-1 mb-0">
                Select a record to open it, or use the menu for more actions.
              </p>
            </div>
          </div>
        </div>

        {entryTabs?.length > 0 && (
          <Tabs
            value={tabIndex}
            onChange={(_e, v) => setTabIndex(v)}
            variant="scrollable"
            scrollButtons
            allowScrollButtonsMobile
            sx={{ ...getThemedTabsSx(), borderBottom: 1, borderColor: "divider", px: 1 }}
          >
            {entryTabs.map((tab) => {
              const count = tabCounts[tab] ?? 0;
              const label = config.listTabLabels?.[tab] ?? tab;
              return (
                <Tab
                  key={tab}
                  label={
                    <span className="inline-flex items-center gap-1.5">
                      {label}
                      <span className="text-[10px] font-semibold opacity-60">{count}</span>
                    </span>
                  }
                  sx={{ fontWeight: 600, fontSize: 12, minHeight: 44 }}
                />
              );
            })}
          </Tabs>
        )}

        {initialLoading ? (
          <ul className="divide-y divide-app-border">
            <EntryRowSkeleton />
            <EntryRowSkeleton />
            <EntryRowSkeleton />
          </ul>
        ) : records.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
            <div className="w-12 h-12 rounded-full bg-app-surface-muted flex items-center justify-center mb-3 text-app-text-muted">
              <DescriptionOutlined fontSize="small" />
            </div>
            <p className="text-sm font-semibold text-app-text">No entries yet</p>
            <p className="text-xs text-app-text-secondary mt-1 max-w-sm">
              Create the first {config.deleteItemLabel?.toLowerCase() || "record"} for {clientName}{" "}
              to get started.
            </p>
            <Link
              to={`${config.basePath}/add`}
              state={addState}
              className="mt-5 inline-flex items-center gap-1.5 rounded-lg bg-primary px-3.5 py-2 text-xs font-semibold text-white hover:opacity-90 transition-opacity"
            >
              <FaPlusCircle />
              {config.newEntryLabel}
            </Link>
          </div>
        ) : filteredRecords.length === 0 ? (
          <div className="py-14 px-6 text-center">
            <p className="text-sm font-medium text-app-text">Nothing in this filter</p>
            <p className="text-xs text-app-text-secondary mt-1">
              Try another tab, or create a new entry.
            </p>
            <button
              type="button"
              onClick={() => setTabIndex(0)}
              className="mt-4 text-xs font-semibold text-primary hover:underline"
            >
              Show all
            </button>
          </div>
        ) : (
          <ul className="divide-y divide-app-border">
            {filteredRecords.map((item, index) => {
              const title = config.getEntryTitle(item);
              const subtitle = config.getEntrySubtitle(item, client);
              const status = config.getEntryStatus(item);
              const entryDate = config.getEntryDate(item);

              return (
                <li key={item.id}>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => openEntry(item)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        openEntry(item);
                      }
                    }}
                    className="group flex items-center gap-3 px-4 py-3.5 cursor-pointer hover:bg-app-surface-hover focus:bg-app-surface-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/30 transition-colors"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 min-w-0 flex-wrap">
                        <span className="font-semibold text-sm text-app-text truncate group-hover:text-primary transition-colors">
                          {index + 1}. {title}
                        </span>
                        {status && (
                          <Chip
                            size="small"
                            label={status}
                            variant="outlined"
                            color="primary"
                            sx={{ height: 20, fontSize: 11 }}
                          />
                        )}
                        {item?.migrated && (
                          <Chip
                            size="small"
                            label="Migrated"
                            variant="outlined"
                            color="warning"
                            sx={{ fontSize: 10, height: 20 }}
                          />
                        )}
                      </div>
                      {subtitle && (
                        <p className="text-xs text-app-text-secondary mt-1 truncate">{subtitle}</p>
                      )}
                    </div>

                    <div className="shrink-0 text-right hidden sm:block">
                      <p className="text-[10px] uppercase tracking-wide text-app-text-muted font-semibold">
                        Updated
                      </p>
                      <p className="text-xs text-app-text-secondary mt-0.5">
                        {formatDisplayDate(entryDate)}
                      </p>
                    </div>

                    <Tooltip title="View account">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewAccount();
                        }}
                        className="opacity-70 group-hover:opacity-100"
                      >
                        <AccountBalance fontSize="small" className="!text-primary" />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="More actions">
                      <IconButton
                        size="small"
                        onClick={(e) => openRowMenu(e, item)}
                        className="opacity-70 group-hover:opacity-100"
                      >
                        <MoreVert fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <ChevronRight className="!text-base text-app-text-muted opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeRowMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={() => {
            openEntry(menuItem);
            closeRowMenu();
          }}
          className="!text-sm gap-2"
        >
          <EditOutlined fontSize="small" className="!text-primary" />
          Open / Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleViewAccount();
            closeRowMenu();
          }}
          className="!text-sm gap-2"
        >
          <AccountBalance fontSize="small" className="!text-primary" />
          View account
        </MenuItem>
        {canAssignTask && (
          <MenuItem onClick={() => openAssignTask(menuItem)} className="!text-sm gap-2">
            <FaPlusCircle fontSize="small" className="!text-green-600" />
            Assign task
          </MenuItem>
        )}
        {canDelete && (
          <MenuItem
            onClick={() => {
              setDeleteTarget({ state: true, id: menuItem?.id });
              closeRowMenu();
            }}
            className="!text-sm gap-2 !text-red-600"
          >
            <DeleteOutline fontSize="small" />
            Delete
          </MenuItem>
        )}
      </Menu>

      {deleteTarget.state && (
        <DeleteModal
          open={deleteTarget.state}
          item={config?.deleteItemLabel || "Entry"}
          handleCancel={() => setDeleteTarget({ state: false, id: null })}
          handleDelete={handleDeleteEntry}
          preview={deletePreviewQuery.data ?? null}
          previewLoading={deletePreviewQuery.isFetching}
        />
      )}

      {assignTarget.state && (
        <TaskModal
          open={assignTarget.state}
          modalData={assignTarget.data}
          handleClose={() => setAssignTarget({ state: false, data: null })}
          module={config.taskModule}
          moduleEntryType={config.taskEntryType}
          moduleEntryId={assignTarget.data?.id ?? null}
        />
      )}
    </div>
  );
}
